import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'

export type MemberForYouBoard = 'DASHBOARD' | 'ARCHIVE' | 'BIN'

export type MemberForYouActionPermissions = {
  canChangeStatus: boolean
  canManageProof: boolean
}

export type MemberForYouActionPolicy = {
  isCurrentlyAssignedToActor: boolean
  isDashboardItem: boolean
  canActOnItem: boolean
  canUpdateStatus: boolean
  canUploadProof: boolean
  readOnlyTitle: string
  readOnlyBody: string
  executionScopeCopy: string
}

export const MEMBER_FOR_YOU_COPY = {
  executionScope: 'This surface intentionally limits you to helper-like execution actions: progress, completion, and evidence only.',
  readOnlyTitle: 'Read-only in this view',
  readOnlyBody: 'This item is visible for inspection, but only items currently assigned to you can be progressed or completed from Todos.',
} as const

export const normalizeMemberForYouStatus = (item: ChecklistItem): ChecklistWorkflowStatus =>
  item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : item.assigneeId ? 'ASSIGNED' : 'CREATED')

export const resolveMemberForYouBoard = (item: ChecklistItem): MemberForYouBoard => {
  if (item.board === 'BIN') return 'BIN'
  if (item.board === 'ARCHIVE') return 'ARCHIVE'
  return 'DASHBOARD'
}

export const toIsoTimestamp = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object' && 'toDate' in (value as Record<string, unknown>)) {
    try { return (value as { toDate: () => Date }).toDate().toISOString() } catch { return null }
  }
  return null
}

export const projectMemberForYouAssignedItem = (item: ChecklistItem): HelperAssignedItem => {
  const inferredStatus = normalizeMemberForYouStatus(item)
  const status: ChecklistWorkflowStatus =
    item.assigneeId && inferredStatus === 'CREATED' ? 'ASSIGNED' : inferredStatus
  return {
    ...item,
    effectiveStatus: status,
    updatedAtIso: toIsoTimestamp(item.updatedAt),
  }
}

export const compareMemberForYouDueAscending = (left: HelperAssignedItem, right: HelperAssignedItem): number => {
  const leftDue = Date.parse(left.scheduleEndDate ?? left.endDate ?? left.scheduleStartDate ?? left.startDate ?? '')
  const rightDue = Date.parse(right.scheduleEndDate ?? right.endDate ?? right.scheduleStartDate ?? right.startDate ?? '')
  const normalizedLeft = Number.isFinite(leftDue) ? leftDue : Number.MAX_SAFE_INTEGER
  const normalizedRight = Number.isFinite(rightDue) ? rightDue : Number.MAX_SAFE_INTEGER
  return normalizedLeft - normalizedRight
}

export const resolveMemberForYouActionPolicy = (
  item: ChecklistItem | null,
  actorUid: string | null | undefined,
  permissions: MemberForYouActionPermissions,
): MemberForYouActionPolicy => {
  const isCurrentlyAssignedToActor = Boolean(item && actorUid && item.assigneeId === actorUid)
  const isDashboardItem = Boolean(item && resolveMemberForYouBoard(item) === 'DASHBOARD')
  const canActOnItem = isCurrentlyAssignedToActor && isDashboardItem
  return {
    isCurrentlyAssignedToActor,
    isDashboardItem,
    canActOnItem,
    canUpdateStatus: canActOnItem && permissions.canChangeStatus,
    canUploadProof: canActOnItem && permissions.canManageProof,
    readOnlyTitle: MEMBER_FOR_YOU_COPY.readOnlyTitle,
    readOnlyBody: MEMBER_FOR_YOU_COPY.readOnlyBody,
    executionScopeCopy: MEMBER_FOR_YOU_COPY.executionScope,
  }
}
